import {
  lisbonDate,
  occupancyReportSchema,
  shiftOccupancyDate,
  type OccupancyPeriod,
  type OccupancyReport,
  type OccupancyStats,
} from '@growup/arena-shared';
import { Button, Card, cx, Field, Notice, Select, Table } from '@growup/ui';
import { useEffect, useMemo, useState } from 'react';

import { download, getJson, messageOf } from '../api';
import { formatMinutes } from '../format';

/**
 * Occupancy of the room and of each PC (RF-D3): monitors of the arena and
 * admins. Only totals; no logins or names (RN-D1, RN-A16).
 */

const PERIODS: { id: OccupancyPeriod; label: string }[] = [
  { id: 'day', label: 'Dia' },
  { id: 'week', label: 'Semana' },
  { id: 'month', label: 'Mês' },
  { id: 'year', label: 'Ano' },
];

const WEEKDAYS = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];
const MONTHS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

const longDate = new Intl.DateTimeFormat('pt-PT', {
  timeZone: 'UTC',
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});
const shortDate = new Intl.DateTimeFormat('pt-PT', {
  timeZone: 'UTC',
  day: 'numeric',
  month: 'short',
});
const monthYear = new Intl.DateTimeFormat('pt-PT', {
  timeZone: 'UTC',
  month: 'long',
  year: 'numeric',
});
const asDate = (iso: string) => new Date(`${iso}T12:00:00Z`);

function formatPercent(value: number | null): string {
  if (value === null) return '—';
  return `${(value * 100).toFixed(value < 0.1 && value > 0 ? 1 : 0).replace('.', ',')}%`;
}

function periodTitle(report: OccupancyReport): string {
  switch (report.period) {
    case 'day':
      return longDate.format(asDate(report.from));
    case 'week':
      return `${shortDate.format(asDate(report.from))} a ${shortDate.format(asDate(report.to))} ${report.to.slice(0, 4)}`;
    case 'month':
      return monthYear.format(asDate(report.from));
    case 'year':
      return report.from.slice(0, 4);
  }
}

/** Short label under a bar, and a longer one for its tooltip. */
function bucketLabels(period: OccupancyPeriod, key: string): { short: string; long: string } {
  if (period === 'day') return { short: `${key}h`, long: `${key}:00–${key}:59` };
  if (period === 'year') {
    const m = Number(key.slice(5, 7)) - 1;
    return { short: MONTHS[m] ?? key, long: monthYear.format(asDate(`${key}-01`)) };
  }
  const date = asDate(key);
  const weekday = WEEKDAYS[(date.getUTCDay() + 6) % 7] ?? '';
  return {
    short: period === 'week' ? weekday : String(date.getUTCDate()),
    long: longDate.format(date),
  };
}

export function OccupancyView({ arenaId }: { arenaId: string }) {
  const [period, setPeriod] = useState<OccupancyPeriod>('week');
  const [date, setDate] = useState(() => lisbonDate(new Date()));
  const [report, setReport] = useState<OccupancyReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  const query = `period=${period}&date=${date}`;
  useEffect(() => {
    let live = true;
    getJson(`/api/arena/arenas/${arenaId}/occupancy?${query}`, occupancyReportSchema)
      .then((r) => {
        if (!live) return;
        setReport(r);
        setError(null);
      })
      .catch((e: unknown) => live && setError(messageOf(e)));
    return () => {
      live = false;
    };
  }, [arenaId, query]);

  const today = lisbonDate(new Date());
  const isCurrent = report !== null && report.from <= today && today <= report.to;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <div role="group" aria-label="Período" className="flex border border-gu-line">
          {PERIODS.map((p) => (
            <button
              key={p.id}
              type="button"
              aria-pressed={period === p.id}
              onClick={() => setPeriod(p.id)}
              className={cx(
                'px-3 py-2 font-sub text-label font-bold uppercase',
                period === p.id
                  ? 'bg-gu-accent text-gu-on-accent'
                  : 'text-gu-ink-muted hover:text-gu-ink',
              )}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="secondary"
            size="sm"
            aria-label="Período anterior"
            onClick={() => setDate((d) => shiftOccupancyDate(period, d, -1))}
          >
            ‹
          </Button>
          <Button
            variant="secondary"
            size="sm"
            aria-label="Período seguinte"
            disabled={isCurrent}
            onClick={() => setDate((d) => shiftOccupancyDate(period, d, 1))}
          >
            ›
          </Button>
          {!isCurrent ? (
            <Button variant="ghost" size="sm" onClick={() => setDate(today)}>
              Hoje
            </Button>
          ) : null}
        </div>
        {report ? (
          <h2 className="font-display text-title uppercase first-letter:uppercase">
            {periodTitle(report)}
          </h2>
        ) : null}
        <Button
          className="ml-auto"
          variant="secondary"
          size="sm"
          onClick={() =>
            void download(
              `/api/arena/arenas/${arenaId}/occupancy.csv?${query}`,
              `ocupacao-${report?.from ?? date}-${report?.to ?? date}.csv`,
            ).catch((e: unknown) => setError(messageOf(e)))
          }
        >
          Exportar CSV
        </Button>
      </div>

      {error ? <Notice tone="danger">{error}</Notice> : null}
      {report && !report.hasOpeningHours ? (
        <Notice tone="info">
          Esta arena ainda não tem horário de abertura, por isso não há percentagem de ocupação, só
          horas. Um administrador preenche-o em Configuração, no cartão «No site».
        </Notice>
      ) : null}

      {report ? (
        <>
          <Kpis report={report} isCurrent={isCurrent} />
          <Card headingLevel={3} title="Uso da sala">
            <UsageChart report={report} />
          </Card>
          {report.period !== 'day' ? (
            <Card headingLevel={3} title="Horas de ponta">
              <Heatmap report={report} />
            </Card>
          ) : null}
          <Card headingLevel={3} title="Por PC">
            <PcTable report={report} />
          </Card>
        </>
      ) : null}
    </div>
  );
}

function Kpis({ report, isCurrent }: { report: OccupancyReport; isCurrent: boolean }) {
  const t = report.totals;
  const tiles = [
    {
      label: 'Ocupação',
      value: formatPercent(t.occupancy),
      hint:
        t.occupancy === null
          ? report.hasOpeningHours
            ? 'sala fechada neste período'
            : 'sem horário de abertura'
          : `${String(t.workstations)} PCs no horário de abertura${isCurrent ? ', até agora' : ''}`,
    },
    { label: 'Horas de jovens', value: formatMinutes(t.youthMinutes), hint: null },
    { label: 'Horas de eventos', value: formatMinutes(t.eventMinutes), hint: null },
    { label: 'Sessões', value: String(t.sessions), hint: null },
    { label: 'Jovens diferentes', value: String(t.distinctYouths), hint: null },
  ];
  return (
    <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
      {tiles.map((tile) => (
        <div key={tile.label} className="border border-gu-line bg-gu-raised p-4">
          <dt className="font-sub text-label font-bold uppercase text-gu-ink-muted">
            {tile.label}
          </dt>
          <dd className="mt-1 font-display text-headline tabular-nums">{tile.value}</dd>
          {tile.hint ? <dd className="text-caption text-gu-ink-muted">{tile.hint}</dd> : null}
        </div>
      ))}
    </dl>
  );
}

/** A round number of hours for the top of the axis. */
function niceMaxHours(hours: number): number {
  if (hours <= 1) return 1;
  const step = 10 ** Math.floor(Math.log10(hours));
  for (const m of [1, 2, 2.5, 5, 10]) if (m * step >= hours) return m * step;
  return 10 * step;
}

const axisHours = (h: number) => `${String(Math.round(h * 10) / 10).replace('.', ',')}h`;

/** Stacked bars (youths, then events) in plain HTML, so text stays crisp at any width. */
function UsageChart({ report }: { report: OccupancyReport }) {
  const [hover, setHover] = useState<number | null>(null);
  const buckets = report.buckets;
  const max = niceMaxHours(
    Math.max(0, ...buckets.map((b) => (b.youthMinutes + b.eventMinutes) / 60)),
  );
  const pct = (minutes: number) => `${String((minutes / 60 / max) * 100)}%`;
  const labelEvery = buckets.length > 24 ? 3 : buckets.length > 12 ? 2 : 1;
  const active = hover === null ? null : buckets[hover];

  return (
    <div className="flex flex-col gap-2">
      <ul className="flex flex-wrap gap-4 text-caption text-gu-ink-muted" aria-label="Legenda">
        <li className="flex items-center gap-2">
          <span aria-hidden="true" className="inline-block h-3 w-3 rounded-sm bg-gu-accent" />
          Jovens
        </li>
        <li className="flex items-center gap-2">
          <span aria-hidden="true" className="inline-block h-3 w-3 rounded-sm bg-gu-grey" />
          Eventos
        </li>
      </ul>
      <div className="flex gap-2">
        <div
          aria-hidden="true"
          className="flex h-48 w-10 shrink-0 flex-col justify-between text-right text-caption tabular-nums text-gu-ink-muted"
        >
          <span className="-translate-y-1/2">{axisHours(max)}</span>
          <span>{axisHours(max / 2)}</span>
          <span className="translate-y-1/2">0h</span>
        </div>
        <div className="relative min-w-0 flex-1" onMouseLeave={() => setHover(null)}>
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 top-0 flex h-48 flex-col justify-between"
          >
            <div className="border-t border-gu-line" />
            <div className="border-t border-gu-line" />
            <div className="border-t border-gu-line" />
          </div>
          <ol className="relative flex h-48 items-end" aria-label="Horas de uso">
            {buckets.map((b, i) => {
              const labels = bucketLabels(report.period, b.key);
              return (
                <li
                  key={b.key}
                  tabIndex={0}
                  aria-label={`${labels.long}: ${formatMinutes(b.youthMinutes)} de jovens, ${formatMinutes(b.eventMinutes)} de eventos, ocupação ${formatPercent(b.occupancy)}`}
                  onMouseEnter={() => setHover(i)}
                  onFocus={() => setHover(i)}
                  onBlur={() => setHover(null)}
                  className={cx(
                    'flex h-full min-w-0 flex-1 flex-col-reverse items-stretch gap-0.5 px-0.5 sm:px-1 outline-none focus-visible:ring-2 focus-visible:ring-gu-focus',
                    hover === i && 'bg-gu-ink/5',
                  )}
                >
                  {b.youthMinutes > 0 ? (
                    <span
                      className="block max-w-12 self-center rounded-t-sm bg-gu-accent"
                      style={{ height: pct(b.youthMinutes), width: '100%' }}
                    />
                  ) : null}
                  {b.eventMinutes > 0 ? (
                    <span
                      className="block max-w-12 self-center rounded-t-sm bg-gu-grey"
                      style={{ height: pct(b.eventMinutes), width: '100%' }}
                    />
                  ) : null}
                </li>
              );
            })}
          </ol>
          <div aria-hidden="true" className="flex">
            {buckets.map((b, i) => (
              <span
                key={b.key}
                className="min-w-0 flex-1 overflow-hidden pt-1 text-center text-caption text-gu-ink-muted"
              >
                {i % labelEvery === 0 ? bucketLabels(report.period, b.key).short : ''}
              </span>
            ))}
          </div>
          {active ? (
            <div
              role="status"
              className="pointer-events-none absolute right-0 top-0 border border-gu-line bg-gu-ground px-3 py-2 text-caption shadow"
            >
              <p className="font-bold first-letter:uppercase">
                {bucketLabels(report.period, active.key).long}
              </p>
              <BucketLines stats={active} />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function BucketLines({ stats }: { stats: OccupancyStats }) {
  return (
    <>
      <p>Jovens: {formatMinutes(stats.youthMinutes)}</p>
      <p>Eventos: {formatMinutes(stats.eventMinutes)}</p>
      <p>Sessões: {stats.sessions}</p>
      <p>Ocupação: {formatPercent(stats.occupancy)}</p>
    </>
  );
}

function Heatmap({ report }: { report: OccupancyReport }) {
  const [hover, setHover] = useState<{ weekday: number; hour: number; busyPcs: number } | null>(
    null,
  );
  // Only the hours the room opens or was used, so the grid stays readable.
  const shown = report.heatmap.filter((c) => c.open || c.busyPcs > 0);
  const first = shown.length ? Math.min(...shown.map((c) => c.hour)) : 10;
  const last = shown.length ? Math.max(...shown.map((c) => c.hour)) : 21;
  const hoursRange = Array.from({ length: last - first + 1 }, (_, i) => first + i);
  const max = Math.max(0, ...report.heatmap.map((c) => c.busyPcs));
  const cell = (weekday: number, hour: number) =>
    report.heatmap.find((c) => c.weekday === weekday && c.hour === hour);

  return (
    <div className="flex flex-col gap-2">
      <p className="text-caption text-gu-ink-muted">
        Média de PCs ocupados em cada hora. Mais escuro, mais PCs; riscado, sala fechada.
      </p>
      <div className="overflow-x-auto">
        <table className="border-separate border-spacing-0.5 text-caption">
          <caption className="sr-only">Média de PCs ocupados por dia da semana e hora</caption>
          <thead>
            <tr>
              <th scope="col" />
              {hoursRange.map((h) => (
                <th key={h} scope="col" className="px-1 font-normal text-gu-ink-muted">
                  {h}h
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {WEEKDAYS.map((label, i) => (
              <tr key={label}>
                <th scope="row" className="pr-2 text-left font-normal text-gu-ink-muted">
                  {label}
                </th>
                {hoursRange.map((h) => {
                  const c = cell(i + 1, h);
                  const busy = c?.busyPcs ?? 0;
                  const strength = max > 0 ? busy / max : 0;
                  const text = `${label} ${String(h)}h: ${busy.toFixed(1).replace('.', ',')} PCs em média${c?.open ? '' : ' (fechado)'}`;
                  return (
                    <td
                      key={h}
                      title={text}
                      aria-label={text}
                      onMouseEnter={() => setHover({ weekday: i + 1, hour: h, busyPcs: busy })}
                      onMouseLeave={() => setHover(null)}
                      className={cx(
                        'h-7 min-w-8 rounded-sm',
                        !c?.open &&
                          'bg-[repeating-linear-gradient(135deg,rgb(var(--gu-line))_0_2px,transparent_2px_6px)]',
                      )}
                      style={
                        busy > 0
                          ? {
                              backgroundColor: `rgb(var(--gu-accent) / ${String(0.12 + 0.88 * strength)})`,
                            }
                          : c?.open
                            ? { backgroundColor: 'rgb(var(--gu-sunken))' }
                            : undefined
                      }
                    />
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="min-h-5 text-caption" aria-live="polite">
        {hover
          ? `${WEEKDAYS[hover.weekday - 1] ?? ''} ${String(hover.hour)}h: ${hover.busyPcs.toFixed(1).replace('.', ',')} PCs ocupados em média`
          : ''}
      </p>
    </div>
  );
}

type SortKey = 'name' | 'occupancy' | 'hours' | 'sessions';

function PcTable({ report }: { report: OccupancyReport }) {
  const [sort, setSort] = useState<SortKey>('name');
  const rows = useMemo(() => {
    const list = [...report.workstations];
    const used = (w: OccupancyStats) => w.youthMinutes + w.eventMinutes;
    if (sort === 'occupancy') list.sort((a, b) => (b.occupancy ?? -1) - (a.occupancy ?? -1));
    if (sort === 'hours') list.sort((a, b) => used(b) - used(a));
    if (sort === 'sessions') list.sort((a, b) => b.sessions - a.sessions);
    return list;
  }, [report, sort]);

  return (
    <div className="flex flex-col gap-3">
      <Field label="Ordenar por" className="max-w-xs">
        <Select value={sort} onChange={(e) => setSort(e.target.value as SortKey)}>
          <option value="name">Nome do PC</option>
          <option value="occupancy">Ocupação</option>
          <option value="hours">Horas de uso</option>
          <option value="sessions">Sessões</option>
        </Select>
      </Field>
      <Table
        caption="Ocupação por PC"
        rowKey={(r) => r.id}
        rows={rows}
        empty="Esta arena ainda não tem PCs."
        columns={[
          {
            key: 'pc',
            header: 'Posto',
            cell: (r) => (
              <>
                {r.name}
                {r.retired ? <em className="ml-1 text-gu-ink-muted">retirado</em> : null}
              </>
            ),
          },
          {
            key: 'youth',
            header: 'Jovens',
            align: 'right',
            cell: (r) => formatMinutes(r.youthMinutes),
          },
          {
            key: 'event',
            header: 'Eventos',
            align: 'right',
            hideOnMobile: true,
            cell: (r) => formatMinutes(r.eventMinutes),
          },
          {
            key: 'sessions',
            header: 'Sessões',
            align: 'right',
            hideOnMobile: true,
            cell: (r) => r.sessions,
          },
          {
            key: 'occupancy',
            header: 'Ocupação',
            cell: (r) => (
              <div className="flex min-w-32 items-center gap-2">
                <div className="h-2 flex-1 rounded-sm bg-gu-sunken" aria-hidden="true">
                  <div
                    className="h-2 rounded-sm bg-gu-accent"
                    style={{ width: `${String(Math.round((r.occupancy ?? 0) * 100))}%` }}
                  />
                </div>
                <span className="w-12 text-right tabular-nums">{formatPercent(r.occupancy)}</span>
              </div>
            ),
          },
        ]}
      />
    </div>
  );
}
